import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useMutation } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

interface RegenerateArchitectureEmbeddingsRequest {
  projectUuid: string;
  regenerate?: boolean;
}

interface RegenerateArchitectureEmbeddingsResponse {
  success: boolean;
  message: string;
}

/**
 * Regenerate embeddings for the architectural graph of a project
 */
async function regenerateArchitectureEmbeddings(
  data: RegenerateArchitectureEmbeddingsRequest,
  token: string
): Promise<RegenerateArchitectureEmbeddingsResponse> {
  const regenerate = data.regenerate ?? true;
  const response = await apiFetch(
    `${config.isometricApiUrl}/architectural-graph/${data.projectUuid}/regenerate-embeddings?regenerate=${regenerate}&llmPlatform=${config.llmPlatform}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: "",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to regenerate embeddings.");
  }

  return response.json();
}

/**
 * Hook to regenerate embeddings for a project's architectural graph
 */
export function useRegenerateArchitectureEmbeddings(
  onSuccess?: (data: RegenerateArchitectureEmbeddingsResponse) => void,
  onError?: (error: Error) => void
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (data: RegenerateArchitectureEmbeddingsRequest) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return regenerateArchitectureEmbeddings(data, token);
    },
    onSuccess,
    onError,
  });
}
